import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveDynamicComponent as _resolveDynamicComponent, createBlock as _createBlock, Transition as _Transition, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "trial-banner",
  role: "status"
}

import { computed, onBeforeUnmount, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useStore } from '@/composables/useStore'
import { useUiStore } from '@/stores/uiStore'
import { useAuthStore } from '@/stores/authStore'
import { useOnboardingStore } from '@/stores/onboardingStore'
import archiWebAuthService from './services/archiweb.auth.service'
import Notification from './components/notifications/Notification.vue'
import AppModal from './components/base/Modal.vue'
import NotificationContainer from './components/notifications/NotificationContainer.vue'
import Toast from 'primevue/toast'
import ConfirmDialog from 'primevue/confirmdialog'

// Store and router

export default /*@__PURE__*/_defineComponent({
  __name: 'App',
  setup(__props) {

/// <reference types="vue" />
// @ts-ignore
const store = useStore()
const uiStore = useUiStore()
const auth = useAuthStore()
const onboarding = useOnboardingStore()
const router = useRouter()
const route = useRoute()
const { t } = useI18n()

// Computed properties
const isAuthenticated = computed(() => auth.isAuthenticated)
const isFirmOwner = computed(() => auth.user?.type === 'FIRM_OWNER')
const showTrialBanner = computed(() => isAuthenticated.value && isFirmOwner.value && onboarding.isTrial && !route.path.startsWith('/onboarding'))
const getError = computed(() => store.getters.getError.value)
const getModal = computed(() => uiStore.getModal)

const isRTL = computed(() => {
  // Check if the current language is RTL (Arabic)
  return (window as any).i18n?.global?.locale?.value === 'ar'
})

const isDevelopment = computed(() => {
  return process.env.NODE_ENV === 'development'
})

// Methods
const handleSignIn = async (): Promise<void> => {
  try {
    await archiWebAuthService.login()
  } catch (error) {
    console.error('Login error:', error)
    store.commit('SET_ERROR', 'Failed to start login process')
  }
}

const clearError = (): void => {
  store.commit('SET_ERROR', null)
}

const handleModalConfirm = (): void => {
  // Execute the callback provided in the modal config
  if (getModal.value && typeof getModal.value.onConfirm === 'function') {
    getModal.value.onConfirm()
  }
  uiStore.setModal(null)
}

const handleModalCancel = (): void => {
  if (getModal.value && typeof getModal.value.onCancel === 'function') {
    getModal.value.onCancel()
  }
  uiStore.setModal(null)
}

// Lifecycle hooks
onMounted(() => {
  // Debug i18n configuration
  console.log('=== App.vue i18n Debug ===')
  console.log('$i18n exists:', !!t)
  console.log('Current locale:', (window as any).i18n?.global?.locale?.value)
  console.log('Available locales:', (window as any).i18n?.global?.availableLocales)
  console.log('Translation test - dashboard.welcome:', t('dashboard.welcome'))
  console.log('Translation test - common.loading:', t('common.loading'))
  console.log('Window.i18n exists:', !!(window as any).i18n)
  console.log('Window.i18n locale:', (window as any).i18n?.global?.locale?.value)

  // Check authentication status on app initialization
  // This is already being handled in main.js with store.dispatch('checkAuth')

  // If we're at the home page and already authenticated, redirect to user home
  if (isAuthenticated.value && route.path === '/') {
    router.push('/')
  }
})// Watchers
watch(isAuthenticated, async (newValue: boolean) => {
  if (newValue && isFirmOwner.value && !onboarding.status) {
    try {
      await onboarding.refresh()
    } catch (error) {
      console.warn('Unable to refresh onboarding status for trial banner', error)
    }
  }
  if (newValue && route.path === '/') {
    router.push('/')
  }
}, { immediate: true })

watch(showTrialBanner, (visible: boolean) => {
  document.documentElement.classList.toggle('trial-banner-visible', visible)
}, { immediate: true })

onBeforeUnmount(() => {
  document.documentElement.classList.remove('trial-banner-visible')
})

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!
  const _component_router_view = _resolveComponent("router-view")!

  return (_openBlock(), _createElementBlock("div", {
    id: "app",
    class: _normalizeClass(["app", { 'rtl': isRTL.value }])
  }, [
    (showTrialBanner.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createElementVNode("span", null, _toDisplayString(_unref(t)('onboarding.trialBanner', { days: _unref(onboarding).status?.remainingTrialDays ?? 0 })), 1),
          _createVNode(_component_router_link, { to: "/onboarding/plan" }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref(t)('onboarding.choosePlan')) + " →", 1)
            ]),
            _: 1
          })
        ]))
      : _createCommentVNode("", true),
    _createVNode(_component_router_view, null, {
      default: _withCtx(({ Component }) => [
        _createVNode(_Transition, {
          name: "page-transition",
          mode: "out-in"
        }, {
          default: _withCtx(() => [
            Component
              ? (_openBlock(), _createElementBlock("div", {
                  key: _unref(route).fullPath,
                  class: "page-transition-wrapper"
                }, [
                  (_openBlock(), _createBlock(_resolveDynamicComponent(Component), { onSignIn: handleSignIn }, null, 32))
                ]))
              : _createCommentVNode("", true)
          ]),
          _: 2
        }, 1024)
      ]),
      _: 1
    }),
    (getError.value)
      ? (_openBlock(), _createBlock(Notification, {
          key: 1,
          message: getError.value,
          type: "error",
          onClose: clearError
        }, null, 8, ["message"]))
      : _createCommentVNode("", true),
    (getModal.value)
      ? (_openBlock(), _createBlock(AppModal, {
          key: 2,
          title: getModal.value.title,
          message: getModal.value.message,
          "confirm-text": getModal.value.confirmText,
          "cancel-text": getModal.value.cancelText,
          "is-visible": !!getModal.value,
          onConfirm: handleModalConfirm,
          onCancel: handleModalCancel
        }, null, 8, ["title", "message", "confirm-text", "cancel-text", "is-visible"]))
      : _createCommentVNode("", true),
    _createVNode(NotificationContainer, {
      position: "top-right",
      "max-notifications": 5
    }),
    _createVNode(_unref(Toast)),
    _createVNode(_unref(ConfirmDialog))
  ], 2))
}
}

})